'use client'

import { useLayoutEffect, useRef, ReactNode } from 'react'
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'

gsap.registerPlugin(ScrollTrigger)

type PropsType = {
  children: ReactNode
  animation?:
    | 'fade-up'
    | 'fade-down'
    | 'fade-left'
    | 'fade-right'
    | 'scale'
    | 'scale-rotate'
    | 'slide-left'
    | 'slide-right'
    | 'slide-up'
    | 'slide-down'
    | 'blur-in'
    | 'blur-out'
  className?: string
  scrollOut?: boolean
  immediate?: boolean
  startOffset?: number
  endOffset?: number
}

const AnimatedSection = ({
  children,
  animation = 'fade-up',
  className = '',
  scrollOut = false,
  immediate = true,
  startOffset,
  endOffset
}: PropsType) => {
  const ref = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    const el = ref.current

    const animConfig: Record<string, { from: gsap.TweenVars; to: gsap.TweenVars }> = {
      'fade-up': { from: { y: 60, opacity: 0 }, to: { y: 0, opacity: 1 } },
      'fade-down': { from: { y: -60, opacity: 0 }, to: { y: 0, opacity: 1 } },
      'fade-left': { from: { x: 80, opacity: 0 }, to: { x: 0, opacity: 1 } },
      'fade-right': { from: { x: -80, opacity: 0 }, to: { x: 0, opacity: 1 } },
      'slide-left': { from: { x: 120, opacity: 0 }, to: { x: 0, opacity: 1 } },
      'slide-right': { from: { x: -120, opacity: 0 }, to: { x: 0, opacity: 1 } },
      'slide-up': { from: { y: 100, opacity: 0 }, to: { y: 0, opacity: 1 } },
      'slide-down': { from: { y: -100, opacity: 0 }, to: { y: 0, opacity: 1 } },
      scale: { from: { scale: 0.9, opacity: 0 }, to: { scale: 1, opacity: 1 } },
      'scale-rotate': { from: { scale: 0.8, rotation: -10, opacity: 0 }, to: { scale: 1, rotation: 0, opacity: 1 } },
      'blur-in': { from: { filter: 'blur(10px)', opacity: 0 }, to: { filter: 'blur(0px)', opacity: 1 } },
      'blur-out': { from: { filter: 'blur(0px)', opacity: 1 }, to: { filter: 'blur(10px)', opacity: 0 } }
    }

    const config = animConfig[animation]
    let ctx: gsap.Context

    if (scrollOut) {
      ctx = gsap.context(() => {
        gsap.fromTo(
          el,
          { y: 0, x: 0, scale: 1, opacity: 1 },
          {
            ...config.from,
            scrollTrigger: {
              trigger: el,
              start: 'top top',
              end: 'bottom top',
              scrub: true
            }
          }
        )
      }, ref)
    } else {
      ctx = gsap.context(() => {
        const defaultStart = immediate ? 85 : 90
        // Anchor both ends to the section's TOP crossing a fixed viewport band
        // (start% near the bottom → end% upper-middle), independent of section
        // height. The old `end: 'bottom bottom'` spanned ≈ the section's own height,
        // so a short heading revealed in a tiny sliver at the very bottom of the
        // screen and never visibly reversed on scroll-up. A fixed top%→top% band
        // plays the whole reveal while the section is centrally in view, so it
        // animates in AND visibly reverses. clamp() keeps both ends inside the
        // scroll range so the first section (below a full-viewport hero) and the
        // last section still reach progress 0/1 and fully reverse.
        const startPos = `clamp(top ${startOffset ?? defaultStart}%)`
        const endPos = `clamp(top ${endOffset ?? 45}%)`

        gsap.fromTo(el, config.from, {
          ...config.to,
          scrollTrigger: {
            trigger: el,
            start: startPos,
            end: endPos,
            scrub: true
          }
        })
      }, ref)
    }

    return () => ctx.revert()
  }, [animation, scrollOut, immediate, startOffset, endOffset])

  return (
    <section ref={ref} className={className} style={{ opacity: 0 }}>
      {children}
    </section>
  )
}

export default AnimatedSection
